import { TablePreference } from './../../../search/models/table-preference.model';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { NavigationService } from '../../../../core/navigation.service';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchEvent } from '../../../../shared/search/models/search-event';
import { NEW_SEARCH_EVENT, ITEM_CLICK_EVENT, NEW_ROW_EVENT, PAGE_OVERFLOW_EVENT } from '../../../../shared/search/search-constants';
import { SearchStatus } from '../../../../shared/search/models/search-status.model';
import { ResourceService } from 'sce-core';
import { SearchableEntityDataService } from '../../../services/searchable-entity/searchable-entity-data.service';
import { SearchableEntityServiceFactory } from '../../../services/searchable-entity/searchable-entity-service-factory';
import { SearchableEntityButtonService } from '../../../services/searchable-entity/searchable-entity-button.service';
import { SearchableEntityConfig } from '../../../models/searchable-entity-config';
import { AlertService } from 'sce-core';
import { ActivatedRoute } from '@angular/router';
import { PlatformLocation, Location } from '@angular/common';
import { UserPreferenceService } from '../../../../layout/home/services/user-preference.service';
import { DELETE_FAVOURITE, ADD_FAVOURITE, UserPreferenceEvent, ADD_RECENT } from '../../../models/user-favourite-event';
import { SavedSearchService } from '../../../search/saved-search.service';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../shared-service-registry';
import { DataTableEditorService } from '../../../search/results/data-table-editor.service';
import { SearchCriteria, Condition, Clause } from '../../../search/models/search-filter-model';
import { UserService } from '../../../../core/security/user.service';
import { menuIdRouteMappings } from 'sce-core/constants';
import { META_DATA, TABLE_NAME } from '../../../../constants';
import { OutboundDataSharingService } from '../../../../features/outbound/outbound-data-sharing.service';

@Component({
  selector: 'lfwms-searchable-entity-search',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.css']
})
export class SearchableEntitySearchComponent implements OnInit, OnDestroy {

  // The factory for all the required services
  @Input() public serviceFactory: SearchableEntityServiceFactory;

  // The configuration object for the entity
  public entityConfig: SearchableEntityConfig;

  // helper services
  protected navService: NavigationService;
  protected resourceService: ResourceService;
  public savedSearchService: SavedSearchService;
  protected alertService: AlertService;
  protected userPreferenceService: UserPreferenceService;

  // entity services
  public dataService: SearchableEntityDataService;
  public buttonService: SearchableEntityButtonService;
  public dataEditor: DataTableEditorService;

  private favouritePreference: any;
  public isFavourite: boolean = false;
  public expandFilterAccordion: boolean = true;
  protected expandResultsAccordion: boolean = false;
  protected searchEventSubscription: Subscription;
  protected userPreferenceEventSubscription: Subscription;
  public searchService: SearchService;
  public searchStatus: SearchStatus;
  public successMsg: string = '';
  public panel: any;
  public default: any;
  public showSearchResults: any;
  public filterAccordionTitle: string = '';
  public resultsAccordionTitle: string = '';

  public tooltipPosition: string = 'bottom';
  public tooltipMessage: string = 'Add to Favourites';
  public storerKey: string;

  constructor(private coreServiceRegistry: CoreServiceRegistry, private sharedServiceRegistry: SharedServiceRegistry,
    private activatedRoute: ActivatedRoute, public platformLocation: PlatformLocation,
    public location: Location, private userService: UserService,
    private outboundSharingDataService: OutboundDataSharingService) {
    this.navService = coreServiceRegistry.navigationService;
    this.resourceService = coreServiceRegistry.resourceService;
    this.userPreferenceService = coreServiceRegistry.userPreferencesService;
    this.savedSearchService = sharedServiceRegistry.savedSearchService;
    this.alertService = sharedServiceRegistry.alertService;
  }

  public ngOnInit() {
    this.initializeProperties();
    this.subscribeForEvents();
    const detailPagePresent = this.dataService.getEntityConfiguration().getDetailPageRoute();
    this.activatedRoute.params.subscribe(params => {
      const searchId = params['searchId'];
      this.expandFilterAccordion = true;
      // if there is a search id in the route, load it
      // search ID PRE is to pre populate search page with criteria on load
      if (searchId && searchId === 'PRE') {
        this.prePopulateSearchCriteria();
      }  else if (searchId) {
        this.loadFromSavedSearchCriteria(searchId);
      }

      if (detailPagePresent) {
        this.navService.setPageTitle(this.entityConfig.getItemLabel()
          .concat(' ').concat(this.resourceService.get('lbl_search')));
      } else {
        this.navService.setPageTitle(this.entityConfig.getItemLabel());
      }
    });
    this.dataService.setDisplayMode('V');
    this.buttonService.initializeSearchPageButtons();
    /*[LFWM-2132] - to determine we are in which page when updating some values both in accordion and table
      or when performing any button actions */
    this.dataService.pageType = 'search';
  }

  /**
   * Initialize the component properties
   */
  private initializeProperties() {
    this.dataService = this.serviceFactory.getDataService();
    this.dataEditor = this.serviceFactory.getDataEditor();
    this.buttonService = this.serviceFactory.getButtonService();
    this.entityConfig = this.dataService.getEntityConfiguration();
    this.filterAccordionTitle = this.entityConfig
      .getItemLabel().concat(' ').concat(this.getLabel('lbl_um_Search'));
    this.searchService = this.dataService.getSearchService();
    this.searchStatus = this.searchService.searchStatus;
    this.searchService.searchParams.tableEditor = this.dataEditor;
    this.searchService.clearSavedSearchConfig(); // Clear any old data
    this.searchService.fetchMetadata().subscribe(metadata => {
      this.initializeTablePreferences();
      this.dataService.setMetaDataMap(metadata);
      // if the search filter is not displayed, then do a search on page load
      if (!this.entityConfig.showSearchFilter) {
        if (!this.searchService.getSearchCriteria()) {
          this.searchService.setSearchCriteria(new SearchCriteria());
        }
        this.searchService.performSearch();
      }
    });
  }

  /**
   * Initialize the page based on the user preferences for the current search
   */
  private initializeTablePreferences() {
    this.userPreferenceService
      .fetchTablePreferences(this.searchService
        .searchParams.tableId, 'FAVOURITECONFIG')
      .subscribe((favouritePreference: any) => {
        this.favouritePreference = favouritePreference;
        this.isFavourite = this.favouritePreference.configuration.favourite;
      });
  }

  /**
   * Subscribe for the search and user preference events
   */
  private subscribeForEvents() {
    this.searchEventSubscription = this.searchService.receiveEvent()
      .subscribe((event: SearchEvent) => {
      // LFWM-2045 storerkey is taken for passing it in the loadbypage request
        if (event.selectedProperty === 'sku') {
          this.storerKey = event.selectedRow.cells['storerKey'].cVal;
        }
        if (event.type === NEW_SEARCH_EVENT) {
          // LFWM-3699/LFWM-4208- when search button is clicked form trade returns and Asn, the storerKey entered in search filter
          // is sent to fetch the dynamic button in search table.
          if (this.searchService.searchParams && (this.searchService.searchParams.tableId === 'trtd' || this.searchService.searchParams.tableId === 'asntd')) {
            this.dataService.buttonService.storerKeys = this.searchService.getSearchCriteria().conditions[0].clauses[0].value;
            this.dataService.buttonService.isSearchDynamicBtnExist = false;
            this.dataService.buttonService.searchdDynamicButtons = [];
          }
          // [FCR-13994] - An archive search (Populate From Archive DB) makes a single loadByPage
          // call, using the archive table id resolved by SearchService.getEffectiveTableId().
          // LFWM-3589 used to fire a second call to the live table from here; that has been removed.
          // LFWM-3699- when search button is clicked form trade returns, the storerKey entered in search filter
          // is sent to fetch the dynamic button in search table.
          if (this.searchService.searchParams && this.searchService.searchParams.tableId === 'trtd') {
            this.dataService.buttonService.storerKeys = this.searchService.getSearchCriteria().conditions[0].clauses[0].value;
            this.dataService.buttonService.isSearchDynamicBtnExist = false;
            this.dataService.buttonService.searchdDynamicButtons = [];
          } 
          this.buttonService.initializeSearchPageButtons();
          this.expandFilterAccordion = false;
          this.expandResultsAccordion = true;
          this.showSearchResults = true;
          this.resultsAccordionTitle = this.searchStatus.recordCount + ' '
            .concat(this.getLabel('lbl_result_count'));
        } else if (event.type === ITEM_CLICK_EVENT) {
          if (event.selectedProperty === META_DATA.MBOL_KEY && event.message === TABLE_NAME.SHIPMENT_ORDER) {
            this.showDetailsPageReferenceSO(event.selectedItemId, true);
          } else if (event.selectedProperty === META_DATA.SO_USER_DEFINED_NINE && event.message === TABLE_NAME.SHIPMENT_ORDER) {
            this.showDetailsPageWaveControl(event.selectedItemId, true);
          } else {
            // this.dataService.setPrimarySelectedDocument(event.selectedItemId);
            this.showDetailsPage(event.selectedItemId);
          }
        } else if (event.type === NEW_ROW_EVENT) {
          // Added to autogenerate primary key while adding a new row
          this.dataService.handleAddNewRow(event);
        } else if (event.type === PAGE_OVERFLOW_EVENT) {
          // Saves the rows on page change navigation.
          this.buttonService.performButtonActions('Save');
        }
      });

    this.userPreferenceEventSubscription = this.userPreferenceService.receiveEvent()
      .subscribe((event: UserPreferenceEvent) => {
        if (event.source === 'search') {
          // if this event originated from the search page, ignore it. It will be handled separately
          return;
        }
        if (event.type === DELETE_FAVOURITE) {
          if (!this.favouritePreference.preferenceId
              || (event.preferenceId === this.favouritePreference.preferenceId)) {
            this.isFavourite = false;
            this.favouritePreference.configuration.favourite = false;
            this.favouritePreference.preferenceId = null;
          }
        }
      });
  }

  /**
   * If the page is loaded using a search criteria id, prepopulate the search filter
   * @param searchId - The search id
   */
  private loadFromSavedSearchCriteria(searchId) {
    // clear the search results
    this.searchStatus.hasResults = false;
    this.savedSearchService.loadSearchCriteria(searchId, this.searchService.getSavedSearchConfig())
      .subscribe((resp: any) => {
        if (resp['statusCode'] && resp['statusMessage']) {
          if (resp['statusCode'] === 200 && resp['statusMessage'] === 'SUCCESS') {
            const formattedCriteria = this.savedSearchService.transformSearchCriteriaToModel(resp, this.searchService);
            if (!formattedCriteria) {
              this.alertService.error('Invalid search criteria');
              return;
            }
            this.searchService.setSearchCriteria(formattedCriteria);
            this.searchService.setNonMandatoryConditionsCount(0);
            this.searchService.getSearchCriteria().conditions.forEach((condition) => {
              if (condition.isMandatory === false) {
                this.searchService.setNonMandatoryConditionsCount(this.searchService.getNonMandatoryConditionsCount() + 1);
              }
            });
          }
        } else {
          if (resp.error) {
            this.alertService.clearAll();
            this.alertService.error(resp.error);
          }
        }
      }, (error) => {
        this.alertService.clearAll().error(error);
      });
  }


  private prePopulateSearchCriteria() {
    // clear the search results
    this.searchStatus.hasResults = false;
    const formattedCriteria = this.userService['SearchCriteriaToPrePopulate'];
    if (!formattedCriteria) {
      this.alertService.error('Invalid search criteria');
      return;
    }
    // populate the search criteria from service
    this.searchService.setSearchCriteria(formattedCriteria);
    this.searchService.setNonMandatoryConditionsCount(0);
    this.searchService.getSearchCriteria().conditions.forEach((condition) => {
      if (condition.isMandatory === false) {
        this.searchService.setNonMandatoryConditionsCount(this.searchService.getNonMandatoryConditionsCount() + 1);
      }
    });
    // get result for prepopulated search criteria
    this.searchService.performSearch();
  }

  // method used to translate the labels
  public getLabel(key) {
    return this.resourceService.get(key);
  }

  /**
   * This method opens the details page for the selected items
   *
   * @param id - item id
   * @param isCreateItem - is it a create event
   */
  protected showDetailsPage(id, isCreateItem?: boolean) {
    if (isCreateItem) {
      this.dataService.setDisplayMode('C');
    } else {
      this.dataService.setDisplayMode('V');
    }
    //  this.navService.navigateByUrl(this.entityConfig.getDetailPageRoute());
    const selectedDetailrows = this.searchService.getSelectedRows();
    let isExternal: boolean;
    if (selectedDetailrows.length > 0) {
      selectedDetailrows.forEach(element => {
        const IdcolumnName = this.searchService.searchParams.idProperty;
        const detailPageUrl = (this.platformLocation as any).location.origin
          + this.location['_baseHref'] +
          this.entityConfig.getDetailPageRoute() + '/'
           +  encodeURIComponent(element.cells[IdcolumnName].cVal);
        // LFWM-3484 Close existing tab page if user click to open the same record again
        if(window['win'+detailPageUrl] != null){
          window['win'+detailPageUrl].close();
        }
        window['win'+detailPageUrl] = window.open(detailPageUrl);
      });
      isExternal = true;
    }
    if (!(this.checkIsSelectedDetail(id, selectedDetailrows))) {
      if (this.storerKey) {
        // LFWM-2045 added storerkey in the navigating URL to access it in SearchableEntityDynamicDetailsComponent
        this.navService.navigateWithRouteParams(this.entityConfig.getDetailPageRoute(), [id, this.storerKey]);
      } else {
        this.navService.navigateWithRouteParams(this.entityConfig.getDetailPageRoute(), [id]);
      }
    }
    this.notifyUserPreferenceEvent(ADD_RECENT, isExternal);
  }

  public checkIsSelectedDetail(selectedId, selectedDetailrows) {
    let isIdSelected = false;
    selectedDetailrows.map(element => {
      const IdcolumnName = this.searchService.searchParams.idProperty;
      if (selectedId === element.cells[IdcolumnName].cVal) {
        isIdSelected = true;
      }
    });
    return isIdSelected;
  }

  // add/remove favorite config
  public toggleFavourites() {
    // if the page is set as favourite
    if (!this.isFavourite) {
      this.favouritePreference.configuration.favourite = true;
      this.userPreferenceService
        .saveTablePreferences(this.favouritePreference)
        .subscribe((data: any) => {
          this.isFavourite = true;
          this.favouritePreference.preferenceId = data['preferenceId'];
          this.notifyUserPreferenceEvent(ADD_FAVOURITE);
          this.alertService.success(this.resourceService.get('msg_fav_add'));
        }, (error: any) => {
          this.alertService.clearAll().error(this.resourceService
            .get(error.error.errors['0'].validationMessage));
        });
    } else {
      this.userPreferenceService
        .deleteFavorites(this.favouritePreference.preferenceId)
        .subscribe((data: any) => {
          this.notifyUserPreferenceEvent(DELETE_FAVOURITE);
          this.isFavourite = false;
          // Resetting favourites flag logic will be handled inside above event callback
          this.alertService.success(this.resourceService.get('msg_fav_del'));
        });
    }
  }

  private notifyUserPreferenceEvent(type: number, isExternal?: boolean) {
    const event = new UserPreferenceEvent();
    event.type = type;
    event.source = 'search';
    event.preferenceId = type;
    if (isExternal) {
      event.isExternal = isExternal;
    }
    this.userPreferenceService.notifyEvent(event);
  }

  public toggleFilterAccordion() {
    this.expandFilterAccordion = !this.expandFilterAccordion;
  }

  public toggleResultsAccordion() {
    this.expandResultsAccordion = !this.expandResultsAccordion;
  }

  /**
   * Handle the button actions on page. The call is delegated to the corresponding button service
   */
  public handleButtonPanelActions(panelId: string, action: any) {
    this.buttonService.performButtonActions(action);
  }

  /**
   * Cleanup the subscriptions
   */
  public ngOnDestroy() {
    this.searchService.setNonMandatoryConditionsCount(0);
    if (this.searchEventSubscription) {
      this.searchEventSubscription.unsubscribe();
    }
    if (this.userPreferenceEventSubscription) {
      this.userPreferenceEventSubscription.unsubscribe();
    }
  }

  // [LFWM-2697] - method called on changes of populate from archive db checkbox
  public toggleArchiveDbCheckbox() {
    this.searchService.searchParams.fetchArchiveRecords = (this.searchService.searchParams.fetchArchiveRecords
      === false) ? true : false;
  }

  protected showDetailsPageReferenceSO(id: string, isBlank: boolean) {
    this.navService.navigateWithRouteParams(menuIdRouteMappings.so_reference, [id], isBlank);
  }
  protected showDetailsPageWaveControl(id: string, isBlank: boolean) {
    this.navService.navigateWithRouteParams(menuIdRouteMappings.wave_control_details, [id], isBlank);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/components/searchable-entity/search-page/search-page.component.ts